import { ArrowLeftOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Form, Input, Row, Select, Typography } from 'antd';
import { CreateDishComponentSection } from './component-section.form';
import CustomLabel from '@/components/custom-label';
import { useCallback, useEffect, useState } from 'react';
import { useProductCache } from '@/hooks/use-product-cache.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  createDish,
  getListDish,
  getListDishSalt,
  uploadImage,
  watchListDishAge,
  watchListDishCookingMethod,
  watchListSourceDish,
  watchListStructureMealDish,
  watchListRegionDish,
  watchListTagDish,
  watchListDish,
  resetMessages,
  watchDishMessage,
  watchDishLoading,
  watchListSizeDish,
  getListDishAge,
  getListDishCookingMethod,
  getListSourceDish,
  getListStructureMealDish,
  getListDishRegion,
  getListDishTag,
  getListSizeDish
} from '@/redux/slices/dish.slice';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import { setMessages } from '@/redux/slices/app.slice';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import CKEditorMaster from '@/components/CKEditor/CKEditorMaster';
import { REQUEST_VALIDATION } from '@/constants/message.constant';
import ProductInfoSection from './ProductInfoSection';

type Size = {
  sizeType: string;
  image?: string;
  dataBig: {
    keyFormula: number;
    data: {
      keyComponent: number,
      ingredient: { name: string, label: string },
      weight: { name: string },
      processingMethod: { name: string },
    }[]
  }[]
}

type UploadResponseItem = {
  linker: { size: string };
  url: string;
};

type sizeImage = {
  size: string;
}

type ImageUploadItem = {
  linker: sizeImage;
  image: string;
};

const DishManagementCreateForm = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [sizes, setSizes] = useState<Size[]>([]);
  const listRegion = useAppSelector(watchListRegionDish);
  const listDishAge = useAppSelector(watchListDishAge);
  const listDishCookingMethod = useAppSelector(watchListDishCookingMethod);
  const listSourceDish = useAppSelector(watchListSourceDish);
  const listStructureMealDish = useAppSelector(watchListStructureMealDish);
  const listTagDish = useAppSelector(watchListTagDish);
  const listDish = useAppSelector(watchListDish);
  const messageInfo = useAppSelector(watchDishMessage);
  const loading = useAppSelector(watchDishLoading);
  const listSizeDish = useAppSelector(watchListSizeDish);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [isProductInfoExpanded, setIsProductInfoExpanded] = useState(false);
  const [activeProductIndex, setActiveProductIndex] = useState<number>(0);

  const avnProducts = useProductCache(sizes);

  const { push } = useQuery();

  const ageDishGroupOptions = listDishAge.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const dishCookingMethodGroupOptions = listDishCookingMethod.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const sourceDishGroupOptions = listSourceDish.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const structureMealDishGroupOptions = listStructureMealDish.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const regionDishGroupOptions = listRegion.map((item) => ({
    value: item.regionId,
    label: item.regionName
  }));

  const tagDishGroupOptions = listTagDish.map((item) => ({
    value: item.tagName,
    label: item.tagName
  }));

  const labelColSpan = 10;

  const onFinish = async (values: any) => {

    let regionValue = values.region;

    if (Array.isArray(values.region)) {
      regionValue = values.region[0]?.value;
    } else if (values.region && typeof values.region === 'object') {
      regionValue = values.region.value;
    }

    if (sizes.length === 0) {
      setSizeError('Vui lòng thêm cỡ món ăn!');
      return;
    }
    setSizeError(null);

    const transformSizes = await transformSizesForPayload(sizes);
    const body: any = {
      ID: values.id,
      NAME: values.name,
      AGE_GROUP_ID: values.age,
      REGION_ID: regionValue,
      EXTERNAL_DISH_TYPE_ID: values.externalSource,
      COOKING_METHOD_ID: values.cookingMethod,
      COOKING_INSTRUCTION: values.processingMethod ?? '',
      TIPS: values.tip ?? '',
      MEAL_STRUCTURE_ID: values.structuralGroup,
      SMALL_IMAGE: "",
      LARGE_IMAGE: "",
      sizes: transformSizes,
      tags: values.label,
      avnProducts: []
    }
    await dispatch(createDish(body));
    await dispatch(getListDish({}));
    await dispatch(getListDishSalt());
    push(PageURLs.ofDishManagement());
  };

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofDishManagement());
  }, [push]);

  const handleProductClick = useCallback((idx: number) => {
    setActiveProductIndex(idx);
    setIsProductInfoExpanded(true);
  }, []);

  const transformSizesForPayload = async (sizes: Size[]) => {
    const images: ImageUploadItem[] = sizes
      .filter((size): size is Size & { image: string } => !!size.image)
      .map((size) => ({
        linker: { size: size.sizeType },
        image: size.image,
      }));

    const uploadedResult = images.length
      ? await dispatch(uploadImage({ images })).unwrap()
      : [];

    const imageMap = new Map(
      (uploadedResult as UploadResponseItem[]).map((item) => [
        item.linker.size,
        item.url,
      ])
    );

    return sizes.map((size) => ({
      SIZE_TYPE: size.sizeType,
      IMAGE: imageMap.get(size.sizeType) || '',
      salt_formulas: size.dataBig.map((formula) => ({
        FORMULA_TYPE: `CTM${formula.keyFormula + 1}`,
        ingredients: formula.data.map((component) => ({
          INGREDIENT_ID: typeof component.ingredient === 'object' && component.ingredient !== null
            ? component.ingredient.name
            : component.ingredient || "",
          WEIGHT: typeof component.weight === 'object' && component.weight !== null
            ? component.weight.name
            : component.weight || "",
          PROCESSING_METHOD: typeof component.processingMethod === 'object' && component.processingMethod !== null
            ? component.processingMethod.name
            : component.processingMethod || "",
        })),
      })),
    }));
  };


  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  useEffect(() => {
    dispatch(getListDishAge());
    dispatch(getListDishCookingMethod());
    dispatch(getListSourceDish());
    dispatch(getListStructureMealDish());
    dispatch(getListDishRegion());
    dispatch(getListDishTag());
    dispatch(getListSizeDish());
  }, [dispatch]);


  return (
    <Form form={form} css={rootStyles} onFinish={onFinish}>
      <Row css={boxStyles} align='middle' gutter={16}>
        <Button
          type='text'
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
        >
          {' '}
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            Tự nhập từng món ăn
          </Typography.Title>
        </Button>
      </Row>

      <Row css={boxStyles} align='middle' gutter={16}>
        <Row css={fieldStyles}>
          <Form.Item
            name={'age'}
            label={CustomLabel({ text: 'Độ tuổi', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn độ tuổi'
              options={ageDishGroupOptions}
              onChange={() => {
                form?.validateFields(['id']);
              }}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'id'}
            label={CustomLabel({ text: 'ID món ăn', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[
              { required: true, message: 'Vui lòng điền thông tin!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const selectedAge = form?.getFieldValue('age');
                  const isDuplicated = listDish.some(
                    (dish) => dish.dishId === value && dish.ageGroupId === selectedAge
                  );
                  return isDuplicated
                    ? Promise.reject(REQUEST_VALIDATION.ERROR_ID)
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder='Nhập ID món ăn' />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'name'}
            label={CustomLabel({ text: 'Tên món ăn', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Input placeholder='Nhập tên món ăn' />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'region'}
            label={CustomLabel({ text: 'Món ăn theo miền', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn miền món ăn'
              options={regionDishGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'label'}
            label={'Gắn nhãn món ăn'}
            labelCol={{ span: labelColSpan }}
          >
            <Select
              mode='tags'
              optionFilterProp='label'
              allowClear
              placeholder='Chọn nhãn món ăn'
              options={tagDishGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'externalSource'}
            label={CustomLabel({ text: 'Nguồn món ăn bên ngoài', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nguồn món ăn'
              options={sourceDishGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'cookingMethod'}
            label={CustomLabel({
              text: 'Nhóm món ăn theo phương pháp nấu',
              isRequired: true
            })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nhóm món ăn theo phương pháp nấu'
              options={dishCookingMethodGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'structuralGroup'}
            label={CustomLabel({
              text: 'Nhóm món ăn theo cấu trúc bữa ăn',
              isRequired: true
            })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nhóm món ăn theo cấu trúc bữa ăn'
              options={structureMealDishGroupOptions}
            />
          </Form.Item>
        </Row>
      </Row>

      <Row css={boxStyles} align='middle' gutter={16}>
        <CreateDishComponentSection sizeRecordData={sizes} setSizes={setSizes} listSizeDish={listSizeDish} onAddSize={() => setSizeError(null)} form={form} />
        {sizeError && (
          <Typography.Text type="danger">
            {sizeError}
          </Typography.Text>
        )}
      </Row>

      <Row css={boxStyles} align='middle' gutter={16}>
        <Typography.Title level={5}>Công thức nấu</Typography.Title>

        <Form.Item
          label='Cách chế biến'
          name={`processingMethod`}
          labelCol={{ span: 24 }}
          css={wideQuillFormItem}
        >
          <div css={localCKEditorStyles}>
            <CKEditorMaster
              key={form.getFieldValue('processingMethod')}
              content={form.getFieldValue('processingMethod')}
              handleUpdateContent={(val: string) => form.setFieldsValue({ processingMethod: val })}
              isDisabled={false}
              MAX_CHAR_LENGTH={5000}
              placeholder="Nhập công thức nấu của món ăn"
            />
          </div>
        </Form.Item>
        <Form.Item
          label='Mẹo hay mách nhỏ'
          name={`tip`}
          labelCol={{ span: 24 }}
          css={wideQuillFormItem}
        >
          <div css={localCKEditorStyles}>
            <CKEditorMaster
              key={form.getFieldValue('tip')}
              content={form.getFieldValue('tip')}
              handleUpdateContent={(val: string) => form.setFieldsValue({ tip: val })}
              isDisabled={false}
              MAX_CHAR_LENGTH={5000}
              placeholder="Nhập công thức nấu của món ăn"
            />
          </div>
        </Form.Item>
        <Form.Item
          label='Thông tin thêm'
          name={`extra`}
          labelCol={{ span: 24 }}
        >
          <div css={extraInfoStyles}>
            {avnProducts.length > 0 ? (
              <>
                <p>Sản phẩm AVN</p>
                <div>
                  {avnProducts.map((item, idx) => (
                    <div 
                      key={`${item?.idFood}-${idx}`} 
                      css={extraInfoCardStyles}
                      onClick={() => handleProductClick(idx)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={item?.logo || item?.thumbnail || IMAGE_DEFAULT} alt={item?.name} />
                      <div>
                        <p>{item?.name}</p>
                        <p className="short-desc">{item.shortDesc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ gridColumn: '1 / -1' }}>Hệ thống tự động hiển thị sản phẩm AVN có trong danh sách thực phẩm của công thức món ăn</p>
            )}

          </div>
        </Form.Item>

        {isProductInfoExpanded && (
          <ProductInfoSection
            isExpanded={isProductInfoExpanded}
            onToggle={() => setIsProductInfoExpanded(false)}
            products={avnProducts}
            initialIndex={activeProductIndex}
          />
        )}
      </Row>

      <Row css={groupButtonStyles} align='top' gutter={16}>
        <Button
          type='default'
          size='large'
          onClick={handleGoBack}
        >
          Hủy
        </Button>

        <Form.Item noStyle>
          <Button type='primary' size='large' htmlType='submit' loading={loading}>
            Lưu
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
};
export default DishManagementCreateForm;

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const fieldStyles = css`
  @media (max-width: 1024px) {
    width: 100%;
  }

  width: 60%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ant-form-item {
    width: 100%;
    margin-bottom: 6px;

    .ant-form-item-row {
      display: flex;
      justify-content: space-between;
    }

    .ant-form-item-label {
      text-align: start !important;

      label {
        flex: 1;
        white-space: normal;
        height: auto;
        line-height: 1.5;

        &::after,
        &::before {
          display: none !important;
        }
      }
    }
  }

  input {
    flex: 1;
  }
`;

const groupButtonStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  gap: 1.6rem;

  button {
    padding: 1rem 10rem;
  }
`;

const localCKEditorStyles = css`
  margin-top: 8px;
  ul li,
  ol li,
  nav ul {
    list-style: revert !important;
  }

  .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
    padding: 0.5rem 2.8rem;
  }
`;

const extraInfoStyles = css`
  display: grid;
  grid-template-columns: 1fr 8fr;
  gap: 1rem;
  width: 100%;

  > p {
    font-family: Roboto;
    font-weight: 400;
    font-style: italic;
    font-size: 14px;
    color: rgba(199, 199, 199, 1);
  }

  > div {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
  }
`

const extraInfoCardStyles = css`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem;
  margin-bottom: 1rem;
  border: 1px solid var(--gray-light-color);
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    border-color: #1890ff;
  }

  svg {
    width: 4.8rem;
    height: 4.8rem;
  }

  p {
    font-family: Roboto;
    font-weight: 400;
    font-style: italic;
    font-size: 14px;
    color: rgba(24, 144, 255, 1);
  }

  img {
    width: 4.8rem;
    height: 4.8rem;
    object-fit: cover;
  }

  .short-desc {
    color: var(--text-color, #333);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 22rem;
  }
`

const wideQuillFormItem = css`
  width: 100%;
`;
